import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Configuration automatique de l'API
// Utilise HTTPS en production pour éviter les problèmes de Mixed Content
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export default function ListeMatchs() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    // Changer le titre de la page
    document.title = 'ESMANAGER - LISTE DES MATCHS';
    
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/matches`);
      const data = await response.json();
      setMatches(data);
    } catch (error) {
      console.error('Erreur chargement matchs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Séparer les matchs en cours et terminés
  const ongoingMatches = matches.filter(m => m.status === 'en_cours');
  const finishedMatches = matches.filter(m => m.status === 'termine');

  // Fonction de tri
  const sortMatches = (matchList) => {
    return [...matchList].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === 'date') {
        aValue = new Date(a.date_creation);
        bValue = new Date(b.date_creation);
      } else if (orderBy === 'score') {
        // Trier par différence de buts
        const aDiff = a.score_home - a.score_away;
        const bDiff = b.score_home - b.score_away;
        aValue = aDiff;
        bValue = bDiff;
      } else if (orderBy === 'resultat') {
        // Trier par résultat: V=2, N=1, D=0
        const getResultValue = (match) => {
          if (match.score_home > match.score_away) return 2;
          if (match.score_home === match.score_away) return 1;
          return 0;
        };
        aValue = getResultValue(a);
        bValue = getResultValue(b);
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const sortedOngoingMatches = sortMatches(ongoingMatches);
  const sortedFinishedMatches = sortMatches(finishedMatches);

  const getResultChip = (scoreHome, scoreAway, status) => {
    if (status !== 'termine') {
      return <Chip label="En cours" color="warning" size="small" />;
    }
    
    if (scoreHome > scoreAway) {
      return <Chip label="V" color="success" size="small" />;
    } else if (scoreHome < scoreAway) {
      return <Chip label="D" color="error" size="small" />;
    } else {
      return <Chip label="N" color="default" size="small" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      {/* Header */}
      <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2, 
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <IconButton 
            onClick={() => navigate('/')} 
            sx={{ 
              p: 1,
              minWidth: 'auto',
              width: 'auto'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <img src="/assets/logo_esma.png" alt="logo" style={{ height: '40px', marginLeft: 'auto', marginRight: 'auto' }} />
          <Box sx={{ width: '48px' }} />
        </Box>

      {/* Contenu */}
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', my: 3 }}>
          Liste des matchs
        </Typography>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 145px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'date'}
                    direction={orderBy === 'date' ? order : 'asc'}
                    onClick={() => handleSort('date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'label_adversaire'}
                    direction={orderBy === 'label_adversaire' ? order : 'asc'}
                    onClick={() => handleSort('label_adversaire')}
                  >
                    Adversaire
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'score'}
                    direction={orderBy === 'score' ? order : 'asc'}
                    onClick={() => handleSort('score')}
                  >
                    Score
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'match_type'}
                    direction={orderBy === 'match_type' ? order : 'asc'}
                    onClick={() => handleSort('match_type')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'lieu'}
                    direction={orderBy === 'lieu' ? order : 'asc'}
                    onClick={() => handleSort('lieu')}
                  >
                    Lieu
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'resultat'}
                    direction={orderBy === 'resultat' ? order : 'asc'}
                    onClick={() => handleSort('resultat')}
                  >
                    Rés.
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Matchs en cours - En haut avec fond coloré */}
              {sortedOngoingMatches.map((match) => (
                <TableRow
                  key={match.id}
                  hover
                  sx={{ 
                    cursor: 'pointer',
                    bgcolor: '#fff3e0' // Fond orange clair pour les matchs en cours
                  }}
                  onClick={() => navigate(`/match-detail/${match.id}`)}
                >
                  <TableCell>{formatDate(match.date_creation)}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{match.label_adversaire}</TableCell>
                  <TableCell align="center">
                    {match.score_home} - {match.score_away}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={match.match_type} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{match.lieu}</TableCell>
                  <TableCell align="center">
                    {getResultChip(match.score_home, match.score_away, match.status)}
                  </TableCell>
                </TableRow>
              ))}
              
              {/* Séparateur visuel si des matchs en cours existent */}
              {sortedOngoingMatches.length > 0 && sortedFinishedMatches.length > 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ bgcolor: '#f5f5f5', py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', pl: 1 }}>
                      Matchs terminés
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              
              {/* Matchs terminés */}
              {sortedFinishedMatches.map((match) => (
                <TableRow
                  key={match.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/match-detail/${match.id}`)}
                >
                  <TableCell>{formatDate(match.date_creation)}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{match.label_adversaire}</TableCell>
                  <TableCell align="center">
                    {match.score_home} - {match.score_away}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={match.match_type} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{match.lieu}</TableCell>
                  <TableCell align="center">
                    {getResultChip(match.score_home, match.score_away, match.status)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {matches.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Aucun match trouvé
            </Typography>
          </Box>
        )}
      </Box>
    </div>
  );
}

